import posthog from 'posthog-js';

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const isProduction = process.env.NEXT_PUBLIC_NODE_ENV === 'production';

type PostHogLoadedListener = () => void;

const loadedListeners = new Set<PostHogLoadedListener>();

const notifyPostHogLoaded = (): void => {
  loadedListeners.forEach((listener) => listener());
  loadedListeners.clear();
};

/**
 * Initializes the browser PostHog client once at module scope in production.
 * Pageviews, pageleave, and session recording stay deferred until auth
 * bootstrap finishes (see PostHogAnalyticsLifecycle).
 */
if (typeof window !== 'undefined' && posthogKey && isProduction) {
  posthog.init(posthogKey, {
    api_host: '/set-relay',
    ui_host: 'https://us.posthog.com',
    cross_subdomain_cookie: true,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    session_recording: {
      recordCrossOriginIframes: true,
      maskAllInputs: true,
      maskTextSelector: '.ph-no-capture',
    },
    loaded: () => {
      notifyPostHogLoaded();
    },
  });

  // Sync init paths may already be ready before `loaded` fires.
  if (posthog.__loaded) {
    notifyPostHogLoaded();
  }
}

export { posthog };

export const isPostHogLoaded = (): boolean =>
  typeof window !== 'undefined' && Boolean(posthog.__loaded);

/**
 * Invokes `listener` once PostHog is ready. If already loaded, runs
 * immediately. Returns an unsubscribe function.
 */
export const onPostHogLoaded = (
  listener: PostHogLoadedListener
): (() => void) => {
  if (isPostHogLoaded()) {
    listener();

    return () => undefined;
  }

  loadedListeners.add(listener);

  return () => {
    loadedListeners.delete(listener);
  };
};
